import {
    AppointmentSessionMatch,
    AppointmentSummaryResponse
} from "../api/controllers/medicalIntegrationConfiguration";
import IResult from "../models/result/IResult";

export interface OutboundAppointmentSuccessMetrics {
    denominator: number;
    successfulOutcomes: number;
    rate: number | null;
}

export interface OutboundAppointmentChartMetrics {
    confirmed: number;
    leftMessage: number;
}

export type AppointmentSessionOutcome = 'confirmed' | 'leftMessage';

interface MatchableAppointmentSession {
    sessionId?: string;
    organizationId?: string;
    phoneNumber?: string;
    eventDate?: string;
}

interface TaggedAppointmentEngagement {
    sessionTags?: string[];
    tags?: string[];
}

const toCount = (value: number | undefined): number => {
    return Number.isFinite(value) ? Math.max(Number(value), 0) : 0;
};

const hasCount = (value: number | undefined): boolean => {
    return typeof value === 'number' && Number.isFinite(value);
};

export const hasAppointmentSummaryData = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined
): boolean => {
    if (!summary) return false;

    return Object.values(summary).some(value =>
        typeof value === 'number' && Number.isFinite(value) && value > 0
    );
};

/**
 * Outbound scheduled appointments are defined by outbound booking tags.
 * Count tag occurrences so multi-patient conversations remain accurate.
 */
export const countOutboundScheduledAppointments = (
    records: TaggedAppointmentEngagement[]
): number => records.reduce((total, record) => {
    const tags = record.sessionTags || record.tags || [];
    return total + tags.filter(tag =>
        tag === 'OUTBOUND_APPOINTMENT_BOOKED' ||
        tag === 'OUTBOUND-APPOINTMENT-BOOKED'
    ).length;
}, 0);

export const normalizeAppointmentPhoneNumber = (value: string | undefined): string => {
    const digits = (value || '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const getAppointmentSessionMatchKey = (
    organizationId: string | undefined,
    phoneNumber: string | undefined
): string => `${organizationId || ''}:${normalizeAppointmentPhoneNumber(phoneNumber)}`;

/**
 * Maps Denticon appointment outcomes back to Voicify sessions by organization and
 * normalized phone number. One latest session is selected per matched appointment,
 * preventing retries for the same appointment from inflating the modal totals.
 */
export const getDenticonMatchedAppointmentSessions = <T extends MatchableAppointmentSession>(
    records: T[],
    matches: AppointmentSessionMatch[] | undefined,
    outcome: AppointmentSessionOutcome
): T[] => {
    // An omitted property indicates an older API response. Preserve the original
    // tag-filtered session behavior until the matching contract is available.
    if (matches === undefined) return records;

    const matchCounts = matches.reduce((counts, match) => {
        const includesOutcome = outcome === 'confirmed'
            ? match.confirmedByVoicify
            : match.leftMessageByVoicify;
        const phoneNumber = normalizeAppointmentPhoneNumber(match.phoneNumber);
        if (!includesOutcome || !phoneNumber) return counts;

        const key = getAppointmentSessionMatchKey(match.organizationId, phoneNumber);
        counts.set(key, (counts.get(key) || 0) + 1);
        return counts;
    }, new Map<string, number>());

    const recordsByMatchKey = records.reduce((groups, record) => {
        const phoneNumber = normalizeAppointmentPhoneNumber(record.phoneNumber);
        if (!phoneNumber) return groups;

        const key = getAppointmentSessionMatchKey(record.organizationId, phoneNumber);
        const recordsForKey = groups.get(key) || [];
        recordsForKey.push(record);
        groups.set(key, recordsForKey);
        return groups;
    }, new Map<string, T[]>());

    const selectedRecords = new Set<T>();
    matchCounts.forEach((count, key) => {
        const seenSessionIds = new Set<string>();
        const candidates = (recordsByMatchKey.get(key) || []).filter(record => {
            if (!record.sessionId) return true;
            if (seenSessionIds.has(record.sessionId)) return false;
            seenSessionIds.add(record.sessionId);
            return true;
        });
        candidates
            .slice()
            .sort((left, right) => {
                const leftTime = Date.parse(left.eventDate || '') || 0;
                const rightTime = Date.parse(right.eventDate || '') || 0;
                return rightTime - leftTime;
            })
            .slice(0, count)
            .forEach(record => selectedRecords.add(record));
    });

    return records.filter(record => selectedRecords.has(record));
};

/**
 * Returns the number of Denticon appointments represented by each selected
 * Voicify conversation. A single family call can update more than one
 * appointment, even though analytics contains only one conversation record.
 *
 * When there are more matched appointments than distinct tagged sessions for a
 * phone number, the additional appointments are assigned to the latest session.
 */
export const getDenticonAppointmentCountsBySession = <T extends MatchableAppointmentSession>(
    records: T[],
    matches: AppointmentSessionMatch[] | undefined,
    outcome: AppointmentSessionOutcome
): Map<T, number> => {
    const selectedRecords = getDenticonMatchedAppointmentSessions(records, matches, outcome);
    const countsByRecord = new Map<T, number>();

    if (matches === undefined) {
        selectedRecords.forEach(record => countsByRecord.set(record, 1));
        return countsByRecord;
    }

    const appointmentCountsByKey = matches.reduce((counts, match) => {
        const includesOutcome = outcome === 'confirmed'
            ? match.confirmedByVoicify
            : match.leftMessageByVoicify;
        const phoneNumber = normalizeAppointmentPhoneNumber(match.phoneNumber);
        if (!includesOutcome || !phoneNumber) return counts;

        const key = getAppointmentSessionMatchKey(match.organizationId, phoneNumber);
        counts.set(key, (counts.get(key) || 0) + 1);
        return counts;
    }, new Map<string, number>());

    const selectedByKey = selectedRecords.reduce((groups, record) => {
        const phoneNumber = normalizeAppointmentPhoneNumber(record.phoneNumber);
        if (!phoneNumber) return groups;

        const key = getAppointmentSessionMatchKey(record.organizationId, phoneNumber);
        const recordsForKey = groups.get(key) || [];
        recordsForKey.push(record);
        groups.set(key, recordsForKey);
        return groups;
    }, new Map<string, T[]>());

    selectedByKey.forEach((recordsForKey, key) => {
        const appointmentCount = appointmentCountsByKey.get(key) || 0;
        const sortedRecords = recordsForKey.slice().sort((left, right) => {
            const leftTime = Date.parse(left.eventDate || '') || 0;
            const rightTime = Date.parse(right.eventDate || '') || 0;
            return rightTime - leftTime;
        });

        sortedRecords.forEach((record, index) => {
            const representedAppointments = index === 0
                ? Math.max(appointmentCount - sortedRecords.length + 1, 1)
                : 1;
            countsByRecord.set(record, representedAppointments);
        });
    });

    return countsByRecord;
};

export const getOutboundAppointmentChartMetrics = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined,
    fallback: Partial<OutboundAppointmentChartMetrics> = {}
): OutboundAppointmentChartMetrics => ({
    confirmed: hasCount(summary?.confirmedByVoicify)
        ? toCount(summary?.confirmedByVoicify)
        : toCount(fallback.confirmed),
    leftMessage: hasCount(summary?.leftMessageByVoicify)
        ? toCount(summary?.leftMessageByVoicify)
        : toCount(fallback.leftMessage)
});

/**
 * Calculates the final patient outcome rate for appointments contacted by Voicify.
 *
 * Confirmations after a left message are already included in totalConfirmed, so they
 * must not be added again. missedWithBookingAfterVoicifyCall is also a subset of
 * missedWithSubsequentBooking and must not be added separately. A missed appointment that resulted in a later booking is
 * a recovered outcome and is included in the numerator.
 */
export const calculateOutboundAppointmentSuccess = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined,
    decimalPlaces: number = 0
): OutboundAppointmentSuccessMetrics => {
    if (!summary) {
        return {
            denominator: 0,
            successfulOutcomes: 0,
            rate: null
        };
    }

    const denominator = hasCount(summary.eligibleAppointments)
        ? toCount(summary.eligibleAppointments)
        : Math.max(
            toCount(summary.totalAppointments) - toCount(summary.totalLeftMessage),
            0
        );
    const successfulOutcomes = hasCount(summary.successfulOutcomes)
        ? toCount(summary.successfulOutcomes)
        : toCount(summary.totalConfirmed) +
            toCount(summary.totalCancelled) +
            toCount(summary.totalReschedules) +
            toCount(summary.totalCheckedOut) +
            toCount(summary.missedWithSubsequentBooking);

    if (denominator <= 0) {
        return {
            denominator,
            successfulOutcomes,
            rate: null
        };
    }

    const precision = Math.pow(10, Math.max(decimalPlaces, 0));

    return {
        denominator,
        successfulOutcomes,
        rate: Math.round((successfulOutcomes / denominator) * 100 * precision) / precision
    };
};


export interface OutboundAppointmentSummaryDisplay {
    rate: number | null;
    label: string;
    description?: string;
}

export const getOutboundAppointmentSummaryDisplay = (
    summary: Partial<AppointmentSummaryResponse> | null | undefined,
    outboundCallCount?: number
): OutboundAppointmentSummaryDisplay => {
    if (outboundCallCount === 0) {
        return {
            rate: null,
            label: '—',
            description: 'No outbound calls were recorded for the selected dates and assistants. Select a different date range to view outbound success results.'
        };
    }
    const { rate } = calculateOutboundAppointmentSuccess(summary);
    if (!summary) {
        return {
            rate: null,
            label: 'Unavailable',
            description: 'Appointment data could not be loaded. Refresh the dashboard to try again.'
        };
    }
    if (rate === null) {
        const hasMatches = toCount(summary.totalAppointments) > 0;
        return {
            rate: null,
            label: hasMatches ? 'No eligible appointments' : '—',
            description: hasMatches
                ? 'Appointment data loaded, but no appointments are eligible for the success rate. Appointments whose only final outcome was a voicemail are excluded.'
                : `The appointment summary loaded, but it contains no outbound appointments for the selected dates and assistants. There are no appointment outcomes to calculate a success rate. ${outboundCallCount > 0 ? 'Outbound calls were recorded, but no linked appointment outcomes are available.' : 'This does not mean no outbound calls were made.'}`
        };
    }
    return { rate, label: `${rate}%` };
};

export const MAX_UNAVAILABLE_OFFICE_NAMES_IN_TOOLTIP = 15;

/**
 * Lists the offices whose appointment summary could not be loaded, e.g. " Unavailable: A, B, and 3 more."
 * Returns an empty string when every office loaded.
 */
export const getUnavailableOfficeNamesDescription = (
    failedOfficeNames: string[],
    maxNames: number = MAX_UNAVAILABLE_OFFICE_NAMES_IN_TOOLTIP
): string => {
    const names = Array.from(new Set(failedOfficeNames ?? [])).sort((a, b) => a.localeCompare(b));
    if (names.length === 0) {
        return '';
    }
    const hiddenCount = names.length - maxNames;
    return ` Unavailable: ${names.slice(0, maxNames).join(', ')}${hiddenCount > 0 ? `, and ${hiddenCount} more` : ''}.`;
};

export interface AppointmentSummaryBatchResult {
    summaries: AppointmentSummaryResponse[];
    loadedApplicationIds: string[];
    failedApplicationIds: string[];
}

/**
 * Loads the appointment summary for a batch of applications. The API fails the whole request when
 * any office in it fails, so a failed batch is retried one application at a time to find which
 * applications are actually unavailable. NotFound means none of the applications have outbound
 * calling configured, which is no data rather than a failure.
 */
export const loadAppointmentSummaryBatch = async (
    applicationIds: string[],
    fetchSummary: (applicationIds: string[]) => Promise<IResult<AppointmentSummaryResponse>>
): Promise<AppointmentSummaryBatchResult> => {
    let result: IResult<AppointmentSummaryResponse> = null;
    try {
        result = await fetchSummary(applicationIds);
    } catch {
        result = null;
    }

    if (result?.resultType === 'Ok' && result.data) {
        return { summaries: [result.data], loadedApplicationIds: [...applicationIds], failedApplicationIds: [] };
    }
    if (result?.resultType === 'NotFound') {
        return { summaries: [], loadedApplicationIds: [...applicationIds], failedApplicationIds: [] };
    }
    if (applicationIds.length <= 1) {
        return { summaries: [], loadedApplicationIds: [], failedApplicationIds: [...applicationIds] };
    }

    const singleResults = await Promise.all(applicationIds.map(applicationId =>
        loadAppointmentSummaryBatch([applicationId], fetchSummary)));
    return {
        summaries: singleResults.flatMap(single => single.summaries),
        loadedApplicationIds: singleResults.flatMap(single => single.loadedApplicationIds),
        failedApplicationIds: singleResults.flatMap(single => single.failedApplicationIds)
    };
};
